import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Button, 
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NotesIcon from '@mui/icons-material/Notes';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import clientsApi from '../../api/clientsApi';
import PaginatedTable from '../../components/PaginatedTable';
import SortableTableHeader from '../../components/SortableTableHeader';

const ClientNotes = ({ clientId, client }) => {
  const navigate = useNavigate();
  // Client data is passed as prop, no need for state
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalNotes, setTotalNotes] = useState(0);
  const [sortField, setSortField] = useState('created_date');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch notes data (client data is passed as prop)
      const notesData = await clientsApi.getClientNotes(clientId, {
        skip: page * pageSize,
        limit: pageSize,
        order_by: sortField,
        order_direction: sortDirection
      });
      setNotes(notesData.notes || []);
      setTotalNotes(notesData.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching client notes:', err);
      setError('Failed to load client notes. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId, page, pageSize, sortField, sortDirection]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when changing page size
  };

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(0); // Reset to first page when sorting changes
  };

  const handleBack = () => {
    // This component is embedded in the client detail page
  };

  const handleViewNote = (note) => {
    setSelectedNote(note);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedNote(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString();
  };

  const getNoteTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'medical':
        return 'error';
      case 'treatment':
        return 'warning';
      case 'general':
        return 'primary';
      case 'appointment':
        return 'info';
      case 'billing':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>

      <PaginatedTable
        title={`Notes for ${client?.first_name} ${client?.last_name}`}
        icon={<NotesIcon color="secondary" sx={{ mr: 1 }} />}
        total={totalNotes}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
      >
        {notes.length === 0 ? (
          <Alert severity="info">No notes found for this client.</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableHeader
                    field="note_type"
                    label="Type"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <TableCell>Note Preview</TableCell>
                  <SortableTableHeader
                    field="is_private"
                    label="Privacy"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="created_at"
                    label="Created Date"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notes.map((note) => (
                  <TableRow key={note.id} hover>
                    <TableCell>
                      <Chip 
                        label={note.note_type || 'General'} 
                        color={getNoteTypeColor(note.note_type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {note.note_text 
                          ? note.note_text.substring(0, 150) + (note.note_text.length > 150 ? '...' : '')
                          : 'No content'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {note.is_private && (
                          <LockIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                        )}
                        <Chip 
                          label={note.is_private ? "Private" : "Public"} 
                          color={note.is_private ? "warning" : "success"}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(note.created_date)}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewNote(note)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PaginatedTable>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Note Details</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedNote && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip 
                    label={selectedNote.note_type || 'General'} 
                    color={getNoteTypeColor(selectedNote.note_type)}
                    size="small"
                  />
                  {selectedNote.is_private && (
                    <Chip 
                      label="Private" 
                      color="warning"
                      size="small"
                      icon={<LockIcon />}
                    />
                  )}
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Note Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Type:</Typography>
                    <Typography variant="body1">{selectedNote.note_type || 'General'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Created Date:</Typography>
                    <Typography variant="body1">{formatDate(selectedNote.created_date)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Privacy:</Typography>
                    <Typography variant="body1">{selectedNote.is_private ? 'Private' : 'Public'}</Typography>
                  </Box>
                  {selectedNote.created_by && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Created By:</Typography>
                      <Typography variant="body1">{selectedNote.created_by}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              
              {selectedNote.note_text && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Note Content
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        backgroundColor: 'grey.50',
                        maxHeight: '400px',
                        overflow: 'auto'
                      }}
                    >
                      <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                        {selectedNote.note_text}
                      </Typography>
                    </Paper>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientNotes; 